import type { AxiosResponse } from "axios";
import type {
  Entity,
  OrgWorker,
  Structure,
  Team,
  Unit,
} from "model/OrganizationalStructure";
import { API_ROOT } from "services/baseURLs.config";
import { axiosRequest } from "services/api/api.utils";

export interface OrganizationalStructureListItem {
  readonly companyEntityId: number;
  readonly companyEntityName: string;
  readonly companyUnitId: number;
  readonly companyUnitName: string;
  readonly workGroupId: number;
  readonly workGroupName: string;
}

const fetchCompanyStructure = async (
  companyId: number,
): Promise<AxiosResponse<Structure>> => {
  return await axiosRequest.auth.get<Structure>(
    `${API_ROOT}/companies/${companyId}/structure/v2`,
  );
};

const assignWorkerToTeam = async (
  companyId: number,
  workGroupId: number,
  workerId: number,
): Promise<AxiosResponse<OrgWorker>> => {
  return await axiosRequest.auth.post<OrgWorker>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}/workers/${workerId}/v2`,
  );
};

const createTeam = async (
  companyId: number,
  name: string,
  companyUnitId: number,
): Promise<AxiosResponse<Team>> => {
  return await axiosRequest.auth.post<Team>(
    `${API_ROOT}/companies/${companyId}/workgroups`,
    {
      companyId,
      name,
      companyUnitId,
    },
  );
};

const createEntity = async (
  companyId: number,
  name: string,
): Promise<AxiosResponse<Entity>> => {
  return await axiosRequest.auth.post<Entity>(
    `${API_ROOT}/companies/${companyId}/structure/entities`,
    { name },
  );
};

const createUnit = async (
  companyId: number,
  entityId: number,
  name: string,
): Promise<AxiosResponse<Unit>> => {
  return await axiosRequest.auth.post<Unit>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}/units`,
    { name },
  );
};

const deleteUnit = async (
  companyId: number,
  entityId: number,
  unitId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}/units/${unitId}`,
  );
};

const deleteTeam = async (
  companyId: number,
  workGroupId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}`,
  );
};

const updateTeam = async (
  companyId: number,
  workGroupId: number,
  name: string,
  companyUnitId?: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.put<void>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}`,
    { name, companyUnitId },
  );
};

const updateUnit = async (
  companyId: number,
  entityId: number,
  unitId: number,
  name: string,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.put<void>(
    `${API_ROOT}/companies/${companyId}/structure/entities/${entityId}/units/${unitId}`,
    { name },
  );
};

const unassignWorkerFromWorkGroup = async (
  companyId: number,
  workGroupId: number,
  workerId: number,
): Promise<AxiosResponse<void>> => {
  return await axiosRequest.auth.delete<void>(
    `${API_ROOT}/companies/${companyId}/workgroups/${workGroupId}/workers/${workerId}/v2`,
  );
};

const getWorkerByNumber = async (
  companyId: number,
  phoneNumber: string,
): Promise<AxiosResponse<OrgWorker>> => {
  return await axiosRequest.auth.get<OrgWorker>(
    `${API_ROOT}/companies/${companyId}/workers/v2/phoneNumber/${phoneNumber}`,
  );
};

const getEmployeeNumber = async (
  companyId: number,
): Promise<AxiosResponse<number>> => {
  return await axiosRequest.auth.get<number>(
    `${API_ROOT}/companies/${companyId}/workers/employeeNumber`,
  );
};

const getOrganizationalStructureList = async (
  companyId: number,
): Promise<AxiosResponse<OrganizationalStructureListItem[]>> => {
  return await axiosRequest.auth.get<OrganizationalStructureListItem[]>(
    `${API_ROOT}/companies/${companyId}/structure/list`,
  );
};

export const organizationalStructure = {
  fetchCompanyStructure,
  assignWorkerToTeam,
  createTeam,
  createUnit,
  createEntity,
  deleteUnit,
  deleteTeam,
  updateUnit,
  updateTeam,
  unassignWorkerFromWorkGroup,
  getWorkerByNumber,
  getEmployeeNumber,
  getOrganizationalStructureList,
};
